// ═══════════════════════════════════════════════════════════════
//  CEO Dashboard
//  • Company scorecard KPI tiles
//  • Revenue vs Target trend (12-month)
//  • P&L summary (Budget vs Actual vs Forecast)
//  • Top 5 risk indicators
//  • Rolling EBITDA margin trend
// ═══════════════════════════════════════════════════════════════
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, LineChart, Line, Cell,
} from "recharts";
import KPICard    from "../common/KPICard";
import TrendChart from "../common/TrendChart";
import LoaderComponent from "../../../../libraries/loader/loader";
import { PBExecuteQuery, Q } from "../../../../services/process/PlanningBudgeting/planningBudgetingService";

const SECTION = ({ title, children, action }) => (
  <div style={{
    background: "#fff", borderRadius: 14,
    boxShadow: "0 2px 14px rgba(73,105,171,0.09)",
    padding: "18px 20px", marginBottom: 22,
  }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
      <div style={{ fontWeight: 700, fontSize: 15, color: "#1a2340" }}>{title}</div>
      {action}
    </div>
    {children}
  </div>
);

const PILL = ({ label, value, color }) => (
  <div style={{
    background: `${color}12`, border: `1px solid ${color}40`,
    borderRadius: 8, padding: "8px 16px", minWidth: 120,
  }}>
    <div style={{ fontSize: 10, color: "#999", marginBottom: 2, textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</div>
    <div style={{ fontWeight: 800, fontSize: 18, color }}>{value}</div>
  </div>
);

export default function CEODashboard() {
  const [year,    setYear]    = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [kpis,    setKpis]    = useState([]);
  const [revData, setRevData] = useState([]);
  const [plData,  setPlData]  = useState([]);

  const [userCredentials] = useState(
    JSON.parse(sessionStorage.getItem("userCredentials") || "{}")
  );

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const creds = PBExecuteQuery();
      const [kpiRes, revRes, plRes] = await Promise.all([
        axios.post(creds[1], { query: Q.companyKPIs(userCredentials.selectedentity) }, creds[0]),
        axios.post(creds[1], { query: Q.revenueByMonth(userCredentials.selectedentity, year) }, creds[0]),
        axios.post(creds[1], { query: Q.plSummary(userCredentials.selectedentity, year) }, creds[0]),
      ]);
      setKpis(kpiRes.data   || []);
      setRevData(revRes.data || []);
      setPlData(plRes.data   || []);
    } catch (e) {
      console.error("CEO Dashboard fetch error", e);
    } finally {
      setLoading(false);
    }
  }, [year, userCredentials.selectedentity]);

  useEffect(() => { fetch(); }, [fetch]);

  // ── Derived metrics ──────────────────────────────────────────
  const totalRevBudget  = revData.reduce((s, r) => s + Number(r.target   || 0), 0);
  const totalRevActual  = revData.reduce((s, r) => s + Number(r.actual   || 0), 0);
  const totalRevForecast= revData.reduce((s, r) => s + Number(r.forecast || 0), 0);
  const revenueAchieved = totalRevBudget ? ((totalRevActual / totalRevBudget) * 100).toFixed(1) : 0;

  const totalExpBudget  = plData.filter(p => p.category !== "Revenue").reduce((s,r) => s + Number(r.budget||0), 0);
  const totalExpActual  = plData.filter(p => p.category !== "Revenue").reduce((s,r) => s + Number(r.actual||0), 0);
  const ebitdaBudget    = totalRevBudget - totalExpBudget;
  const ebitdaActual    = totalRevActual - totalExpActual;
  const ebitdaMargin    = totalRevActual ? ((ebitdaActual / totalRevActual) * 100).toFixed(1) : 0;

  // Trend chart data
  const trendData = revData.map((r) => ({
    label:    r.budget_month?.slice(0, 3),
    budget:   Number(r.target   || 0),
    actual:   Number(r.actual   || 0),
    forecast: Number(r.forecast || 0),
    confidence_band: [Number(r.forecast || 0) * 0.88, Number(r.forecast || 0) * 1.12],
  }));

  // Radar chart for KPI scorecard
  const radarData = kpis.slice(0, 6).map((k) => ({
    subject: k.kpi_name?.slice(0, 16),
    actual:  Math.min(120, Number(k.kpi_value || 0)),
    target:  100,
  }));

  // P&L waterfall-style bar data
  const plBarData = plData.map((p) => ({
    name:     p.category,
    Budget:   Number(p.budget   || 0),
    Actual:   Number(p.actual   || 0),
    Forecast: Number(p.forecast || 0),
  }));

  if (loading) return <LoaderComponent loaderOpen />;

  const fmt = (v) => `₹${(v / 10000000).toFixed(2)}Cr`;

  return (
    <div style={{ padding: "6px 2px" }}>

      {/* ── Header bar ── */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: 20, flexWrap: "wrap", gap: 10,
      }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#1a2340" }}>CEO Executive Dashboard</div>
          <div style={{ fontSize: 12, color: "#aaa" }}>Enterprise performance overview · FY {year}</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            style={{
              padding: "6px 12px", borderRadius: 8, border: "1px solid #d0d8ec",
              fontSize: 13, color: "#333", background: "#fff", cursor: "pointer",
            }}
          >
            {[2023, 2024, 2025, 2026].map((y) => <option key={y}>{y}</option>)}
          </select>
          <button onClick={fetch} style={{
            padding: "6px 14px", borderRadius: 8,
            background: "#4969ab", color: "#fff", border: "none",
            fontSize: 12, cursor: "pointer", fontWeight: 600,
          }}>↻ Refresh</button>
        </div>
      </div>

      {/* ── Top KPI summary pills ── */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
        <PILL label="Revenue (Actual)"   value={fmt(totalRevActual)}  color="#4969ab" />
        <PILL label="Revenue (Budget)"   value={fmt(totalRevBudget)}  color="#7b68ee" />
        <PILL label="Forecast Revenue"   value={fmt(totalRevForecast)} color="#e67e22" />
        <PILL label="Revenue Achieved"   value={`${revenueAchieved}%`} color={revenueAchieved >= 90 ? "#2d8c4e" : "#c0392b"} />
        <PILL label="EBITDA (Actual)"    value={fmt(ebitdaActual)}    color="#2d8c4e" />
        <PILL label="EBITDA Margin"      value={`${ebitdaMargin}%`}   color={ebitdaMargin >= 15 ? "#2d8c4e" : "#e67e22"} />
      </div>

      {/* ── KPI Tiles ── */}
      {kpis.length > 0 && (
        <SECTION title="🎯 Company KPI Scorecard">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
            {kpis.map((k) => (
              <KPICard
                key={k.kpi_name}
                title={k.kpi_name}
                value={Number(k.kpi_value || 0)}
                target={Number(k.kpi_target || 0)}
                unit={k.kpi_unit}
                trend={Number(k.variance_percent || 0)}
                color={Number(k.variance_percent) >= 0 ? "#2d8c4e" : "#c0392b"}
              />
            ))}
          </div>
        </SECTION>
      )}

      {/* ── Revenue Trend ── */}
      <SECTION title="📈 Revenue: Budget vs Actual vs Forecast">
        {trendData.length > 0 ? (
          <TrendChart
            data={trendData}
            title=""
            height={300}
            yLabel="Monthly Revenue (₹)"
            showBand
            currentMonthLabel={new Date().toLocaleString("default", { month: "short" })}
          />
        ) : (
          <div style={{ textAlign: "center", color: "#bbb", padding: 40 }}>No revenue data for FY {year}</div>
        )}
      </SECTION>

      {/* ── P&L Summary + Radar ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 22 }}>

        {/* P&L Bar */}
        <SECTION title="📊 P&L Summary – Budget vs Actual">
          {plBarData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={plBarData} margin={{ top: 5, right: 10, left: 10, bottom: 50 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f8" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" interval={0} />
                <YAxis tickFormatter={(v) => `₹${(v / 100000).toFixed(0)}L`} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => `₹${Number(v).toLocaleString("en-IN")}`} />
                <Legend iconSize={9} wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Budget"   fill="#4969ab" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Actual"   fill="#2d8c4e" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Forecast" fill="#e67e22" radius={[4, 4, 0, 0]} opacity={0.8} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: "center", color: "#bbb", padding: 40 }}>No P&L data available</div>
          )}
        </SECTION>

        {/* Radar KPI scorecard */}
        <SECTION title="🕸️ KPI Performance Radar">
          {radarData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#e8ecf4" />
                <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: "#666" }} />
                <Radar name="Actual" dataKey="actual" stroke="#4969ab" fill="#4969ab" fillOpacity={0.25} strokeWidth={2} />
                <Radar name="Target" dataKey="target" stroke="#2d8c4e" fill="none"    strokeDasharray="4 3" strokeWidth={1.5} />
                <Legend iconSize={9} wrapperStyle={{ fontSize: 11 }} />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: "center", color: "#bbb", padding: 40 }}>No KPI data available</div>
          )}
        </SECTION>
      </div>

      {/* ── EBITDA trend line ── */}
      <SECTION title="💹 EBITDA Trend">
        <ResponsiveContainer width="100%" height={200}>
          <LineChart
            data={plBarData}
            margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f8" />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} />
            <YAxis tickFormatter={(v) => `₹${(v/100000).toFixed(0)}L`} tick={{ fontSize: 10 }} />
            <Tooltip formatter={(v) => `₹${Number(v).toLocaleString("en-IN")}`} />
            <Legend iconSize={9} wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="Budget"   stroke="#4969ab" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="Actual"   stroke="#2d8c4e" strokeWidth={2.5} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="Forecast" stroke="#e67e22" strokeWidth={2} strokeDasharray="5 3" dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </SECTION>

      {/* ── Footer ── */}
      <div style={{ fontSize: 10, color: "#ccc", textAlign: "right", marginTop: 4 }}>
        Last refreshed: {new Date().toLocaleString()} · FY {year}
      </div>
    </div>
  );
}
